import { useState, useEffect } from 'react';
import { authService } from '../services/auth';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getFullApiUrl } from '../config/api';

interface Discount {
  code: string;
  status: 'active' | 'inactive';
  type: string;
  value: number;
  validFrom?: string;
  validUntil?: string;
  usedCount: number;
  maxUses: number;
  revenue?: number;
  campaignId?: string;
}

export default function DiscountManagement() {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [solutionFilter, setSolutionFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    loadDiscounts();
  }, [filter, solutionFilter]);

  const loadDiscounts = async () => {
    setLoading(true);
    setError('');
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
        if (token) authService.setToken(token);
      }
      if (!token) {
        setError('Authentication required');
        return;
      }

      const params = filter !== 'all' ? `?status=${filter}` : '';
      const solutionParam = solutionFilter !== 'all' ? `${params ? '&' : '?'}solutionId=${solutionFilter}` : '';
      const response = await fetch(getFullApiUrl(`/admin/discounts${params}${solutionParam}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      setDiscounts(data.discounts || []);
    } catch (err: any) {
      console.error('Load discounts error:', err);
      setError(err.message || 'Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  const toggleDiscount = async (code: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    if (!confirm(`${newStatus === 'active' ? 'Enable' : 'Disable'} discount ${code}?`)) return;

    setToggling(code);
    try {
      let token = authService.getToken();
      if (!token) {
        const session = await fetchAuthSession();
        token = session.tokens?.idToken?.toString() || null;
      }
      if (!token) {
        alert('Authentication required');
        return;
      }

      const response = await fetch(getFullApiUrl(`/admin/discounts/${code}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to toggle');
      
      setDiscounts(prev => prev.map(d => 
        d.code === code ? { ...d, status: newStatus as 'active' | 'inactive' } : d
      ));
      alert(`Discount ${newStatus === 'active' ? 'enabled' : 'disabled'}!`);
    } catch (err: any) {
      alert('Failed to toggle: ' + err.message);
    } finally {
      setToggling(null);
    }
  };

  const filteredDiscounts = discounts.filter(d => 
    search ? d.code.toLowerCase().includes(search.toLowerCase()) : true
  );

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Discount Management</h2>

      {/* Solution Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Solution</label>
        <select
          value={solutionFilter}
          onChange={(e) => setSolutionFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="all">All Solutions</option>
          <option value="aws-solution-finder">AWS Solution Finder (FasII)</option>
        </select>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            All ({discounts.length})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded ${filter === 'active' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
          >
            Active
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-4 py-2 rounded ${filter === 'inactive' ? 'bg-gray-600 text-white' : 'bg-gray-200'}`}
          >
            Inactive
          </button>
        </div>
        <input
          type="text"
          placeholder="Search by code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2 border rounded flex-1 max-w-md"
        />
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && <div className="text-center py-8">Loading...</div>}

      {/* Discounts Table */}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">Code</th>
                <th className="border px-4 py-2 text-left">Status</th>
                <th className="border px-4 py-2 text-left">Type</th>
                <th className="border px-4 py-2 text-left">Value</th>
                <th className="border px-4 py-2 text-left">Usage</th>
                <th className="border px-4 py-2 text-left">Revenue</th>
                <th className="border px-4 py-2 text-left">Valid Until</th>
                <th className="border px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDiscounts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="border px-4 py-8 text-center text-gray-500">
                    No discounts found
                  </td>
                </tr>
              ) : (
                filteredDiscounts.map(discount => (
                  <tr key={discount.code} className="hover:bg-gray-50">
                    <td className="border px-4 py-2 font-mono font-semibold">{discount.code}</td>
                    <td className="border px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        discount.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {discount.status}
                      </span>
                    </td>
                    <td className="border px-4 py-2">{discount.type}</td>
                    <td className="border px-4 py-2">{discount.value}%</td>
                    <td className="border px-4 py-2">
                      {discount.usedCount} / {discount.maxUses}
                    </td>
                    <td className="border px-4 py-2">
                      ₹{(discount.revenue || 0).toFixed(2)}
                    </td>
                    <td className="border px-4 py-2">
                      {discount.validUntil 
                        ? new Date(discount.validUntil).toLocaleDateString() 
                        : 'No expiry'}
                    </td>
                    <td className="border px-4 py-2 text-center">
                      <button
                        onClick={() => toggleDiscount(discount.code, discount.status)}
                        disabled={toggling === discount.code}
                        className={`px-3 py-1 text-xs rounded ${
                          discount.status === 'active'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } disabled:opacity-50`}
                      >
                        {toggling === discount.code ? '...' : (discount.status === 'active' ? 'Disable' : 'Enable')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
